// Stateless HTTP helpers wrapping the backend's agent endpoints.
// Each call takes baseUrl + apiKey explicitly so the React tree can
// re-render the components without a stable client instance.

function headers(apiKey: string): Record<string, string> {
  return apiKey ? { 'X-API-Key': apiKey } : {};
}

function base(baseUrl: string): string {
  return baseUrl.replace(/\/$/, '');
}

async function errMessage(r: Response): Promise<string> {
  const text = await r.text();
  return text ? text.slice(0, 400) : r.statusText;
}

export async function listAgents(baseUrl: string, apiKey: string): Promise<string[]> {
  const r = await fetch(`${base(baseUrl)}/agents`, { headers: headers(apiKey) });
  if (!r.ok) throw new Error(`GET /agents HTTP ${r.status} — ${await errMessage(r)}`);
  const data = (await r.json()) as { agents?: string[] };
  return data.agents ?? [];
}

export async function getAgentMarkdown(
  baseUrl: string,
  apiKey: string,
  agentId: string,
): Promise<string> {
  const url = `${base(baseUrl)}/agents/${encodeURIComponent(agentId)}/markdown`;
  const r = await fetch(url, { headers: headers(apiKey) });
  if (!r.ok) throw new Error(`GET /agents/${agentId}/markdown HTTP ${r.status} — ${await errMessage(r)}`);
  const data = (await r.json()) as { markdown?: string };
  return data.markdown ?? '';
}

export type Provider = 'anthropic' | 'openrouter';

export interface RunOpts {
  provider?: Provider;
  model?: string;
}

export interface RunResult {
  finalText: string;
  raw: unknown;
}

export async function runAgent(
  baseUrl: string,
  apiKey: string,
  agentId: string,
  prompt: string,
  opts: RunOpts = {},
): Promise<RunResult> {
  const url = `${base(baseUrl)}/agents/${encodeURIComponent(agentId)}/run`;
  const body: Record<string, unknown> = {
    prompt,
    provider: opts.provider ?? 'openrouter',
  };
  if (opts.model) body.model = opts.model;
  const r = await fetch(url, {
    method: 'POST',
    headers: { ...headers(apiKey), 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  if (!r.ok) {
    throw new Error(`POST /agents/${agentId}/run HTTP ${r.status} — ${await errMessage(r)}`);
  }
  const data = (await r.json()) as { final_text?: string };
  return { finalText: data.final_text ?? '', raw: data };
}

export interface DefineAgentResult {
  agentId: string;
  agentMdPath: string;
  systemPrompt: string;
}

export async function defineAgent(
  baseUrl: string,
  apiKey: string,
  requirements: string,
  opts: RunOpts = {},
): Promise<DefineAgentResult> {
  const url = `${base(baseUrl)}/define_agent`;
  const body: Record<string, unknown> = {
    requirements,
    provider: opts.provider ?? 'openrouter',
  };
  if (opts.model) body.model = opts.model;
  const r = await fetch(url, {
    method: 'POST',
    headers: { ...headers(apiKey), 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  if (!r.ok) {
    throw new Error(`POST /define_agent HTTP ${r.status} — ${await errMessage(r)}`);
  }
  const data = (await r.json()) as {
    agent_id?: string;
    agent_md_path?: string;
    system_prompt?: string;
  };
  return {
    agentId: data.agent_id ?? '',
    agentMdPath: data.agent_md_path ?? '',
    systemPrompt: data.system_prompt ?? '',
  };
}

export async function deleteAgent(
  baseUrl: string,
  apiKey: string,
  agentId: string,
): Promise<void> {
  const url = `${base(baseUrl)}/agents/${encodeURIComponent(agentId)}`;
  const r = await fetch(url, { method: 'DELETE', headers: headers(apiKey) });
  if (!r.ok) {
    throw new Error(`DELETE /agents/${agentId} HTTP ${r.status} — ${await errMessage(r)}`);
  }
}

export async function putAgentMarkdown(
  baseUrl: string,
  apiKey: string,
  agentId: string,
  markdown: string,
): Promise<void> {
  const url = `${base(baseUrl)}/agents/${encodeURIComponent(agentId)}/markdown`;
  const r = await fetch(url, {
    method: 'PUT',
    headers: { ...headers(apiKey), 'Content-Type': 'application/json' },
    body: JSON.stringify({ markdown }),
  });
  if (!r.ok)
    throw new Error(`PUT /agents/${agentId}/markdown HTTP ${r.status} — ${await errMessage(r)}`);
}

export async function updateAgent(
  baseUrl: string,
  apiKey: string,
  agentId: string,
  requirements: string,
  opts: RunOpts = {},
): Promise<DefineAgentResult> {
  const body: Record<string, unknown> = {
    agent_id: agentId,
    requirements,
    provider: opts.provider ?? 'openrouter',
  };
  if (opts.model) body.model = opts.model;
  const r = await fetch(`${base(baseUrl)}/update_agent`, {
    method: 'POST',
    headers: { ...headers(apiKey), 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  if (!r.ok)
    throw new Error(`POST /update_agent HTTP ${r.status} — ${await errMessage(r)}`);
  const data = (await r.json()) as {
    agent_id?: string;
    agent_md_path?: string;
    system_prompt?: string;
  };
  return {
    agentId: data.agent_id ?? '',
    agentMdPath: data.agent_md_path ?? '',
    systemPrompt: data.system_prompt ?? '',
  };
}

// ---------------- skills CRUD --------------------------------------------

export interface SkillInfo {
  name: string;
  size: number;
  modified_at: string;
}

export interface SkillMarkdown extends SkillInfo {
  agent_id: string;
  user_id: string;
  content: string;
}

export async function listAgentSkills(
  baseUrl: string,
  apiKey: string,
  agentId: string,
): Promise<SkillInfo[]> {
  const r = await fetch(`${base(baseUrl)}/agents/${encodeURIComponent(agentId)}/skills`, {
    headers: headers(apiKey),
  });
  if (!r.ok)
    throw new Error(`GET /agents/${agentId}/skills HTTP ${r.status} — ${await errMessage(r)}`);
  const data = (await r.json()) as { skills?: SkillInfo[] };
  return data.skills ?? [];
}

export async function getAgentSkill(
  baseUrl: string,
  apiKey: string,
  agentId: string,
  name: string,
): Promise<SkillMarkdown> {
  const r = await fetch(
    `${base(baseUrl)}/agents/${encodeURIComponent(agentId)}/skills/${encodeURIComponent(name)}`,
    { headers: headers(apiKey) },
  );
  if (!r.ok)
    throw new Error(`GET skill ${name} HTTP ${r.status} — ${await errMessage(r)}`);
  return (await r.json()) as SkillMarkdown;
}

export async function putAgentSkill(
  baseUrl: string,
  apiKey: string,
  agentId: string,
  name: string,
  content: string,
): Promise<SkillInfo & { created: boolean }> {
  const r = await fetch(
    `${base(baseUrl)}/agents/${encodeURIComponent(agentId)}/skills/${encodeURIComponent(name)}`,
    {
      method: 'PUT',
      headers: { ...headers(apiKey), 'Content-Type': 'application/json' },
      body: JSON.stringify({ content }),
    },
  );
  if (!r.ok)
    throw new Error(`PUT skill ${name} HTTP ${r.status} — ${await errMessage(r)}`);
  return (await r.json()) as SkillInfo & { created: boolean };
}

export async function deleteAgentSkill(
  baseUrl: string,
  apiKey: string,
  agentId: string,
  name: string,
): Promise<void> {
  const r = await fetch(
    `${base(baseUrl)}/agents/${encodeURIComponent(agentId)}/skills/${encodeURIComponent(name)}`,
    { method: 'DELETE', headers: headers(apiKey) },
  );
  if (!r.ok)
    throw new Error(`DELETE skill ${name} HTTP ${r.status} — ${await errMessage(r)}`);
}

export interface ApiKeyRow {
  api_key: string;
  namespace: string;
  label: string | null;
  tools: 'all' | Record<string, unknown>;
  created_at: string;
}

export interface ListApiKeysResult {
  dbPath: string;
  count: number;
  rows: ApiKeyRow[];
}

export async function listApiKeys(baseUrl: string): Promise<ListApiKeysResult> {
  const r = await fetch(`${base(baseUrl)}/api_keys`);
  if (!r.ok) throw new Error(`GET /api_keys HTTP ${r.status} — ${await errMessage(r)}`);
  const data = (await r.json()) as {
    db_path?: string;
    count?: number;
    api_keys?: ApiKeyRow[];
  };
  return {
    dbPath: data.db_path ?? '',
    count: data.count ?? 0,
    rows: data.api_keys ?? [],
  };
}

// ---------------- markdown_files (per-namespace scratch storage) -----------

export interface MarkdownFileInfo {
  name: string;
  size: number;
  modified_at: string;
}

export interface MarkdownFile extends MarkdownFileInfo {
  user_id: string;
  content: string;
}

export async function listMarkdownFiles(
  baseUrl: string,
  apiKey: string,
): Promise<MarkdownFileInfo[]> {
  const r = await fetch(`${base(baseUrl)}/markdown_files`, { headers: headers(apiKey) });
  if (!r.ok) throw new Error(`GET /markdown_files HTTP ${r.status} — ${await errMessage(r)}`);
  const data = (await r.json()) as { files?: MarkdownFileInfo[] };
  return data.files ?? [];
}

export async function getMarkdownFile(
  baseUrl: string,
  apiKey: string,
  name: string,
): Promise<MarkdownFile> {
  const r = await fetch(`${base(baseUrl)}/markdown_files/${encodeURIComponent(name)}`, {
    headers: headers(apiKey),
  });
  if (!r.ok) throw new Error(`GET /markdown_files/${name} HTTP ${r.status} — ${await errMessage(r)}`);
  return (await r.json()) as MarkdownFile;
}

export async function putMarkdownFile(
  baseUrl: string,
  apiKey: string,
  name: string,
  content: string,
): Promise<MarkdownFileInfo & { created: boolean }> {
  const r = await fetch(`${base(baseUrl)}/markdown_files/${encodeURIComponent(name)}`, {
    method: 'PUT',
    headers: { ...headers(apiKey), 'Content-Type': 'application/json' },
    body: JSON.stringify({ content }),
  });
  if (!r.ok) throw new Error(`PUT /markdown_files/${name} HTTP ${r.status} — ${await errMessage(r)}`);
  return (await r.json()) as MarkdownFileInfo & { created: boolean };
}

export async function deleteMarkdownFile(
  baseUrl: string,
  apiKey: string,
  name: string,
): Promise<void> {
  const r = await fetch(`${base(baseUrl)}/markdown_files/${encodeURIComponent(name)}`, {
    method: 'DELETE',
    headers: headers(apiKey),
  });
  if (!r.ok) throw new Error(`DELETE /markdown_files/${name} HTTP ${r.status} — ${await errMessage(r)}`);
}

// ---------------- api_keys ------------------------------------------------

export async function generateApiKey(
  baseUrl: string,
  apiKey: string,
  tools: 'all' | Record<string, unknown> = 'all',
  label?: string,
): Promise<ApiKeyRow & { api_key: string }> {
  const body: Record<string, unknown> = { tools };
  if (label) body.label = label;
  const r = await fetch(`${base(baseUrl)}/generate_api_key`, {
    method: 'POST',
    headers: { ...headers(apiKey), 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  if (!r.ok) throw new Error(`POST /generate_api_key HTTP ${r.status} — ${await errMessage(r)}`);
  return (await r.json()) as ApiKeyRow & { api_key: string };
}

// ---- usage / budget / monitoring (platform pages) ----

export interface KeyUsage {
  api_key: string;
  namespace: string;
  calls: number;
  input_tokens: number;
  output_tokens: number;
  cost_usd: number;
  last_used_at?: string | null;
  budget?: BudgetStatus;
}
export interface BudgetStatus {
  api_key: string;
  cap_usd: number | null;
  period: string;
  spend_usd: number;
  pct_used: number | null;
  over_budget: boolean;
  alerting: boolean;
  blocked: boolean;
}
export interface UsageResult {
  namespace: string;
  admin: boolean;
  totals: { calls: number; input_tokens: number; output_tokens: number; cost_usd: number };
  keys: KeyUsage[];
}
export async function getUsage(baseUrl: string, apiKey: string): Promise<UsageResult> {
  const r = await fetch(`${base(baseUrl)}/usage`, { headers: headers(apiKey) });
  if (!r.ok) throw new Error(`GET /usage HTTP ${r.status} — ${await errMessage(r)}`);
  return (await r.json()) as UsageResult;
}

export interface UsageEvent {
  id: number; api_key: string; namespace: string; ts: string;
  provider: string; model: string; input_tokens: number; output_tokens: number; cost_usd: number;
}
export async function getUsageEvents(baseUrl: string, apiKey: string, limit = 100): Promise<UsageEvent[]> {
  const r = await fetch(`${base(baseUrl)}/usage/events?limit=${limit}`, { headers: headers(apiKey) });
  if (!r.ok) throw new Error(`GET /usage/events HTTP ${r.status} — ${await errMessage(r)}`);
  return ((await r.json()) as { events?: UsageEvent[] }).events ?? [];
}

export interface BudgetInput { cap_usd: number; period: string; alert_pct: number; hard_stop: boolean; }
export async function putBudget(baseUrl: string, apiKey: string, targetKey: string, body: BudgetInput) {
  const r = await fetch(`${base(baseUrl)}/api_keys/${encodeURIComponent(targetKey)}/budget`, {
    method: 'PUT', headers: { ...headers(apiKey), 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  if (!r.ok) throw new Error(`PUT budget HTTP ${r.status} — ${await errMessage(r)}`);
  return r.json();
}

// ---- ClawHub live registry (public API, CORS *) ----
const CLAWHUB = 'https://clawhub.ai/api/v1';
export interface ClawSkill {
  slug: string; name: string; summary: string; version?: string;
  installs?: number; stars?: number; downloads?: number; owner?: string;
}
function str(v: any): string | undefined {
  if (v == null) return undefined;
  if (typeof v === 'string') return v;
  if (typeof v === 'object') return v.version || v.handle || undefined;
  return String(v);
}
function normClaw(it: any): ClawSkill {
  const st = it.stats || {};
  return {
    slug: it.slug, name: it.displayName || it.slug, summary: it.summary || '',
    version: str(it.version) || str(it.latestVersion) || str((it.tags || {}).latest),
    installs: st.installsAllTime ?? it.installs, stars: st.stars ?? it.stars,
    downloads: st.downloads, owner: str(it.ownerHandle) || str(it.owner),
  };
}
export async function clawhubBrowse(sort = 'installsAllTime', limit = 60): Promise<ClawSkill[]> {
  const r = await fetch(`${CLAWHUB}/skills?sort=${sort}&limit=${limit}`);
  if (!r.ok) throw new Error(`ClawHub browse HTTP ${r.status}`);
  return (((await r.json()).items) || []).map(normClaw);
}
export async function clawhubSearch(q: string, limit = 60): Promise<ClawSkill[]> {
  const r = await fetch(`${CLAWHUB}/search?q=${encodeURIComponent(q)}&limit=${limit}`);
  if (!r.ok) throw new Error(`ClawHub search HTTP ${r.status}`);
  return (((await r.json()).results) || []).map(normClaw);
}
export async function clawhubFile(slug: string): Promise<string> {
  const r = await fetch(`${CLAWHUB}/skills/${encodeURIComponent(slug)}/file?path=SKILL.md`);
  if (!r.ok) throw new Error(`ClawHub file HTTP ${r.status}`);
  return r.text();
}
